import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { getCotisations } from '../services/api';
import { notificationService } from '../utils/notifications';
import './ListeMontantsPrint.css';

const ListeCotisationsPrint = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [cotisations, setCotisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAssociation, setSelectedAssociation] = useState('');
  const [statutFilter, setStatutFilter] = useState(''); // 'impaye' = uniquement impayées, 'paye' = payées, '' = toutes

  // Récupérer les paramètres depuis l'URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params.get('search') || '';
    const association = params.get('association') || '';
    const statut = params.get('statut') || '';
    setSearchTerm(search);
    setSelectedAssociation(association);
    setStatutFilter(statut === 'impaye' || statut === 'paye' ? statut : '');
  }, [location]);

  useEffect(() => {
    const loadCotisations = async () => {
      try {
        const response = await getCotisations();
        setCotisations(response.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des cotisations:', error);
        notificationService.error('Erreur', 'Impossible de charger les cotisations');
      } finally {
        setLoading(false);
      }
    };

    loadCotisations();
  }, []);

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;

    try {
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `Liste_Cotisations${selectedAssociation ? `_${selectedAssociation.replace(/\s+/g, '_')}` : ''}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: printRef.current.scrollWidth,
          height: printRef.current.scrollHeight,
          windowWidth: printRef.current.scrollWidth,
          allowTaint: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'landscape'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'], avoid: ['.print-header', '.print-table'] }
      };

      await html2pdf().set(opt).from(printRef.current).save();
      notificationService.success('PDF généré', 'Le fichier PDF a été téléchargé avec succès.');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      notificationService.error('Erreur', 'Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  const handleClose = () => {
    window.close();
    if (!document.hidden) {
      navigate('/admin');
    }
  };

  // Filtrer les cotisations
  const filteredCotisations = cotisations.filter(cotisation => {
    // Filtre par association
    if (selectedAssociation && cotisation.membre?.association !== selectedAssociation) {
      return false;
    }
    
    // Filtre par recherche
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const moisNoms = [
      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
    ];
    const moisNom = moisNoms[cotisation.mois - 1] || '';
    return (
      cotisation.membre?.numero_matricule?.toLowerCase().includes(search) ||
      cotisation.membre?.nom?.toLowerCase().includes(search) ||
      cotisation.membre?.prenom?.toLowerCase().includes(search) ||
      moisNom.includes(search) ||
      cotisation.annee?.toString().includes(search) ||
      cotisation.statut?.toLowerCase().includes(search) ||
      cotisation.montant?.toString().includes(search)
    );
  });

  // Trier les cotisations
  const sortedCotisations = [...filteredCotisations].sort((a, b) => {
    // Trier par membre d'abord
    const nomA = `${a.membre?.nom || ''} ${a.membre?.prenom || ''}`.trim();
    const nomB = `${b.membre?.nom || ''} ${b.membre?.prenom || ''}`.trim();
    if (nomA !== nomB) return nomA.localeCompare(nomB);
    
    // Puis par année
    if (a.annee !== b.annee) return b.annee - a.annee;
    
    // Puis par mois
    if (a.mois !== b.mois) return b.mois - a.mois;
    
    // Puis par semaine
    return (a.semaine || 0) - (b.semaine || 0);
  });

  const moisNoms = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  // Regrouper par membre (une ligne par membre)
  const groupedByMember = useMemo(() => {
    const map = new Map();
    sortedCotisations.forEach(c => {
      const id = c.membre?.id ?? c.membre_id ?? 'unknown';
      if (!map.has(id)) {
        map.set(id, { membre: c.membre, cotisations: [] });
      }
      map.get(id).cotisations.push(c);
    });
    return Array.from(map.values());
  }, [sortedCotisations]);

  // Calculer les statistiques
  const totalCotisations = filteredCotisations.length;
  const cotisationsPayees = filteredCotisations.filter(c => c.statut === 'paye').length;
  const cotisationsImpayees = filteredCotisations.filter(c => c.statut === 'impaye').length;
  const totalMontant = filteredCotisations.reduce((sum, c) => sum + parseFloat(c.montant || 0), 0);
  const montantPaye = filteredCotisations
    .filter(c => c.statut === 'paye')
    .reduce((sum, c) => sum + parseFloat(c.montant || 0), 0);
  const montantImpaye = filteredCotisations
    .filter(c => c.statut === 'impaye')
    .reduce((sum, c) => sum + parseFloat(c.montant || 0), 0);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des cotisations...</p>
      </div>
    );
  }

  return (
    <div className="liste-montants-print-page" style={{ display: 'block', visibility: 'visible' }}>
      {/* Barre d'outils - masquée à l'impression */}
      <div className="print-toolbar" style={{ display: 'flex' }}>
        <div className="toolbar-left">
          <h2>{statutFilter === 'impaye' ? 'Liste des Cotisations Impayées' : statutFilter === 'paye' ? 'Liste des Cotisations Payées' : 'Liste des Cotisations'} - Impression{selectedAssociation ? ` - ${selectedAssociation}` : ''}</h2>
        </div>
        <div className="toolbar-right">
          <button className="btn btn-primary" onClick={handlePrint}>
            🖨️ Imprimer
          </button>
          <button className="btn btn-success" onClick={handleDownloadPDF}>
            📥 Télécharger PDF
          </button>
          <button className="btn btn-secondary" onClick={handleClose}>
            ✕ Fermer
          </button>
        </div>
      </div>

      {/* Contenu à imprimer */}
      <div ref={printRef} className="print-content" style={{ display: 'block', visibility: 'visible', background: 'white' }}>
        {/* En-tête */}
        <div className="print-header">
          <div className="header-content-wrapper">
            <div className="header-logo">
              <img src="/logo.png" alt="Logo" className="logo-img" />
            </div>
            <div className="header-text">
              <h1>MUTU NDE ESPOIR ASBL</h1>
              <h2>Liste des Cotisations{selectedAssociation ? ` - ${selectedAssociation}` : ''}</h2>
              <div className="header-info">
                <p className="print-date">
                  <strong>Date d'édition :</strong> {new Date().toLocaleDateString('fr-FR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                {selectedAssociation && (
                  <p className="print-date" style={{ marginTop: '5px' }}>
                    <strong>Association :</strong> {selectedAssociation}
                  </p>
                )}
                <p className="print-date" style={{ marginTop: '5px' }}>
                  <strong>Total :</strong> {totalCotisations} cotisation(s) - Payées: {cotisationsPayees} - Impayées: {cotisationsImpayees}
                </p>
                <p className="print-date" style={{ marginTop: '5px' }}>
                  <strong>Montants :</strong> Total: {totalMontant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF - 
                  Payé: {montantPaye.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF - 
                  Impayé: {montantImpaye.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau : une ligne par membre, cotisations regroupées */}
        <div className="print-table-container">
          <table className="print-table">
            <thead>
              <tr>
                <th>N°</th>
                <th>Matricule</th>
                <th>Nom complet</th>
                <th>Détail cotisations (Mois/Année · Montant · Statut)</th>
                <th>Total payé (CDF)</th>
                <th>Total impayé (CDF)</th>
              </tr>
            </thead>
            <tbody>
              {groupedByMember.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    {searchTerm || selectedAssociation ? 'Aucune cotisation trouvée' : 'Aucune cotisation enregistrée'}
                  </td>
                </tr>
              ) : (
                groupedByMember.map(({ membre, cotisations }, index) => {
                  const totalPaye = cotisations.filter(c => c.statut === 'paye').reduce((s, c) => s + parseFloat(c.montant || 0), 0);
                  const totalImpaye = cotisations.filter(c => c.statut === 'impaye').reduce((s, c) => s + parseFloat(c.montant || 0), 0);
                  const detail = cotisations.map(c => `${moisNoms[c.mois - 1]} ${c.annee}${c.semaine ? ` (S${c.semaine})` : ''}: ${parseFloat(c.montant || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} CDF · ${c.statut === 'paye' ? 'Payée' : 'Impayée'}`).join(' ; ');
                  return (
                    <tr key={membre?.id ?? index}>
                      <td>{index + 1}</td>
                      <td>{membre?.numero_matricule || '-'}</td>
                      <td>{membre?.nom || ''} {membre?.prenom || ''}</td>
                      <td style={{ fontSize: '0.9em', whiteSpace: 'normal' }}>{detail}</td>
                      <td className="text-right">{totalPaye.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                      <td className="text-right">{totalImpaye.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pied de page */}
        <div className="print-footer">
          <p>Document généré par MUTU NDE ESPOIR ASBL - Système de gestion</p>
        </div>
      </div>
    </div>
  );
};

export default ListeCotisationsPrint;

